import { api } from '@goaltree/backend/convex/_generated/api'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import { cn } from '@goaltree/ui/lib/utils'
import { Link } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import {
  BookOpen,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Compass,
  type LucideIcon,
  Search,
  Sparkles,
} from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CanopyAvatar } from '@/components/canopy-avatar'
import { habitIconComponent } from '@/components/habit-icon'
import { UserAvatar } from '@/components/user-avatar'
import { CATEGORY_COLORS, categoryTextColor, useCategoryFill } from '@/lib/category-color'
import { categoryIcon } from '@/lib/category-icon'

export type TemplateSummary = FunctionReturnType<typeof api.templates.list>[number]
type Sort = 'popular' | 'new'
export type TemplateFilter = string | 'mine' | null
type CardAction = (tpl: TemplateSummary) => ReactNode

const CATEGORY_KEYS = Object.keys(CATEGORY_COLORS)

const BADGE_TONE = {
  blueprints: 'bg-sky-400/15 text-sky-700 dark:text-sky-300',
  classics: 'bg-amber-400/15 text-amber-700 dark:text-amber-300',
  esoteric: 'bg-violet-400/15 text-violet-700 dark:text-violet-300',
  curated: 'bg-primary/15 text-primary-text',
  community: 'border border-border text-muted-foreground',
}

// `pill` is the rounded variant the template cards and detail header use.
export function TemplateBadge({
  tpl,
  pill = false,
  className,
}: {
  tpl: Pick<TemplateSummary, 'source' | 'collection'>
  pill?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const [tone, Icon, label] =
    tpl.collection === 'blueprints'
      ? [BADGE_TONE.blueprints, Compass, t('templates.bp.badge')]
      : tpl.collection === 'classics'
        ? [BADGE_TONE.classics, BookOpen, t('templates.classic')]
        : tpl.collection === 'esoteric'
          ? [BADGE_TONE.esoteric, Sparkles, t('templates.esoteric')]
          : tpl.source === 'editorial'
            ? [BADGE_TONE.curated, null, t('templates.curated')]
            : [BADGE_TONE.community, null, t('templates.community')]
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 font-extrabold tracking-wider uppercase',
        pill ? 'rounded-full px-2 py-1 text-[9.5px]' : 'rounded-[5px] px-1.5 py-px text-[9px]',
        tone,
        className,
      )}
    >
      {Icon && <Icon className="h-2.5 w-2.5" strokeWidth={2.6} />}
      {label}
    </span>
  )
}

export function templateMetaLine(
  t: (key: string, opts?: Record<string, unknown>) => string,
  tpl: Pick<TemplateSummary, 'milestoneCount' | 'weeks' | 'minutesPerDay'>,
) {
  return [
    t('templates.milestones', { count: tpl.milestoneCount }),
    t('templates.weeks', { count: tpl.weeks }),
    t('templates.minPerDay', { count: tpl.minutesPerDay }),
  ].join(' · ')
}

export function templateIcon(
  tpl: Pick<TemplateSummary, 'category'> & { blueprint: { icon?: string | null } | null },
): LucideIcon {
  return tpl.blueprint?.icon ? habitIconComponent(tpl.blueprint.icon) : categoryIcon(tpl.category)
}

export function spanLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  span: TemplateSummary['milestones'][number]['span'],
) {
  if (!span) return ''
  const range = span.from === span.to ? String(span.from) : `${span.from}–${span.to}`
  return t(`templates.card.span.${span.unit}`, { range })
}

export function TemplateIconTile({
  tpl,
  size,
}: {
  tpl: Parameters<typeof templateIcon>[0]
  size: 'md' | 'lg'
}) {
  const fill = useCategoryFill()
  const Icon = templateIcon(tpl)
  return (
    <span
      className={cn(
        'grid shrink-0 place-items-center text-[#0b0c0b]',
        size === 'lg' ? 'size-12 rounded-[14px]' : 'size-10 rounded-xl',
      )}
      style={{ backgroundColor: fill(CATEGORY_COLORS[tpl.category] ?? CATEGORY_COLORS.mind) }}
    >
      <Icon className={size === 'lg' ? 'size-[26px]' : 'size-[22px]'} strokeWidth={2.2} />
    </span>
  )
}

// onSelect turns the card into a button (signed-out visitors can't open a template yet).
export function TemplateCard({
  tpl,
  action,
  onSelect,
}: {
  tpl: TemplateSummary
  action?: CardAction
  onSelect?: (tpl: TemplateSummary) => void
}) {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  const base = CATEGORY_COLORS[tpl.category] ?? CATEGORY_COLORS.mind
  const color = fill(base)
  // The action sits over the footer, beside the card link rather than nested inside it.
  if (action)
    return (
      <div className="relative flex w-full min-w-0">
        <TemplateCard tpl={tpl} />
        <div className="absolute right-3.5 bottom-4 bg-card pl-2">{action(tpl)}</div>
      </div>
    )
  const bp = tpl.blueprint
  // Older backends don't send the row details yet.
  const rows = bp
    ? (bp.habits ?? bp.habitTitles.map((title) => ({ title, time: null, cadence: null }))).map(
        (h) => ({
          title: h.title,
          detail: h.time ?? (h.cadence ? t(`templates.bp.cadence.${h.cadence}`) : ''),
        }),
      )
    : (tpl.milestones ?? tpl.milestoneTitles.map((title) => ({ title, span: null }))).map((m) => ({
        title: m.title,
        detail: spanLabel(t, m.span),
      }))
  const more = bp
    ? t('templates.card.moreHabits', { count: bp.habitCount - rows.length })
    : t('templates.card.moreMilestones', { count: tpl.milestoneCount - rows.length })
  const hasMore = (bp ? bp.habitCount : tpl.milestoneCount) > rows.length
  const sub = bp?.person
    ? t('templates.bp.inspiredBy', { name: bp.person })
    : tpl.inspiredBy
      ? t('templates.inspiredBy', { work: tpl.inspiredBy.work, author: tpl.inspiredBy.author })
      : null
  const meta = bp
    ? [
        t('templates.bp.habits', { count: bp.habitCount }),
        ...(bp.minutesPerDay ? [`~${t('templates.minPerDay', { count: bp.minutesPerDay })}`] : []),
      ].join(' · ')
    : [
        t('templates.weeks', { count: tpl.weeks }),
        t('templates.minPerDay', { count: tpl.minutesPerDay }),
      ].join(' · ')
  const cardClass =
    'flex w-full min-w-0 cursor-pointer flex-col overflow-hidden rounded-[18px] border border-border bg-card text-left transition-colors hover:border-foreground/20'
  const body = (
    <>
      <div
        className="flex h-16 shrink-0 items-start justify-between px-3.5 py-3"
        style={{ backgroundColor: `${color}24` }}
      >
        <TemplateIconTile tpl={tpl} size="md" />
        <TemplateBadge tpl={tpl} pill className="bg-background/70 dark:bg-background/55" />
      </div>
      <div className="flex flex-1 flex-col gap-2 px-3.5 pt-3 pb-3.5">
        <div className="flex flex-col gap-[3px]">
          <span
            className="text-[10px] font-bold tracking-widest uppercase"
            style={{ color: categoryTextColor(base) }}
          >
            {t(`discover.categories.${tpl.category}`, { defaultValue: tpl.category })}
          </span>
          <span className="text-[17px] leading-tight font-extrabold tracking-tight text-foreground">
            {tpl.title}
          </span>
          {sub && <span className="line-clamp-2 text-xs text-muted-foreground">{sub}</span>}
          {tpl.author && (
            <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
              <CanopyAvatar userId={tpl.author.userId} className="shrink-0">
                <UserAvatar
                  image={tpl.author.image}
                  name={tpl.author.name}
                  size={32}
                  className="size-4 rounded-full object-cover"
                />
              </CanopyAvatar>
              <span className="truncate">
                {t('templates.card.byHandle', {
                  handle: tpl.author.handle ?? tpl.author.name ?? '',
                })}
              </span>
            </span>
          )}
        </div>
        <div className="flex flex-col gap-[5px]">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-[12.5px] text-foreground/85">
              <span
                className="size-1.5 shrink-0 rounded-full opacity-70"
                style={{ backgroundColor: color }}
              />
              <span className="min-w-0 flex-1 truncate">{r.title}</span>
              {r.detail && (
                <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                  {r.detail}
                </span>
              )}
            </div>
          ))}
          {tpl.stillGoingPct !== null ? (
            <span className="pl-3.5 text-[11.5px] font-semibold text-primary-text">
              {t('templates.stillGoing', { pct: tpl.stillGoingPct })}
            </span>
          ) : (
            hasMore && <span className="pl-3.5 text-[11.5px] text-muted-foreground">{more}</span>
          )}
        </div>
        <div className="mt-auto pt-1">
          <span className="block truncate font-mono text-[11px] text-muted-foreground">{meta}</span>
        </div>
      </div>
    </>
  )
  return onSelect ? (
    <button type="button" onClick={() => onSelect(tpl)} className={cardClass}>
      {body}
    </button>
  ) : (
    <Link to="/templates/$templateId" params={{ templateId: tpl.id }} className={cardClass}>
      {body}
    </Link>
  )
}

// A horizontal row of cards with snap scrolling, edge fades and (for mice) prev/next arrows.
// `lane` is the larger new-goal heading, with an optional category dot and "See all" link.
function Shelf({
  title,
  hint,
  note,
  items,
  action,
  onSelect,
  dot,
  more,
  lane = false,
}: {
  title: string
  hint?: string
  note?: string
  items: TemplateSummary[]
  action?: CardAction
  onSelect?: (tpl: TemplateSummary) => void
  dot?: string
  more?: ReactNode
  lane?: boolean
}) {
  const { t } = useTranslation()
  const rowRef = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ start: true, end: false })
  const measure = useCallback(() => {
    const el = rowRef.current
    if (!el) return
    const start = el.scrollLeft <= 4
    const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4
    setEdges((e) => (e.start === start && e.end === end ? e : { start, end }))
  }, [])
  useEffect(() => {
    measure()
    const el = rowRef.current
    if (!el) return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [measure, items.length])
  if (!items.length) return null
  const page = (dir: 1 | -1) => {
    const el = rowRef.current
    el?.scrollBy({ left: dir * Math.max(260, el.clientWidth * 0.8), behavior: 'smooth' })
  }
  const fade = `linear-gradient(to right, ${edges.start ? '#000' : 'transparent'}, #000 24px, #000 calc(100% - 40px), ${edges.end ? '#000' : 'transparent'})`
  const arrow =
    'absolute top-1/2 z-10 hidden size-9 -translate-y-1/2 cursor-pointer place-items-center rounded-full border border-border bg-card/95 text-foreground shadow-md backdrop-blur-sm transition-opacity hover:bg-muted [@media(hover:hover)_and_(pointer:fine)]:group-hover/shelf:grid'
  return (
    <section className={cn('flex flex-col', lane ? 'gap-3' : 'gap-2')}>
      <div className="flex items-end justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h3
            className={
              lane
                ? 'flex items-center gap-2 text-[17px] font-extrabold tracking-tight text-foreground'
                : 'text-xs font-semibold uppercase tracking-widest text-foreground/40'
            }
          >
            {dot && (
              <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: dot }} />
            )}
            {title}
          </h3>
          {(hint ?? note) && (
            <p className={cn('text-muted-foreground', lane ? 'text-[12.5px]' : 'text-[11px]')}>
              {hint ?? note}
            </p>
          )}
        </div>
        {more}
      </div>
      <div className="group/shelf relative">
        <div
          ref={rowRef}
          data-noswipe
          onScroll={measure}
          className="-mx-4 flex snap-x snap-mandatory gap-2.5 overflow-x-auto scroll-px-4 px-4 pb-1 lg:mx-0 lg:scroll-px-0 lg:px-0 [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: 'none', maskImage: fade, WebkitMaskImage: fade }}
        >
          {items.map((tpl) => (
            <div key={tpl.id} className="flex w-[250px] shrink-0 snap-start">
              <TemplateCard tpl={tpl} action={action} onSelect={onSelect} />
            </div>
          ))}
        </div>
        {!edges.start && (
          <button
            type="button"
            onClick={() => page(-1)}
            aria-label={t('templates.lanes.prev')}
            className={cn(arrow, '-left-3')}
          >
            <ChevronLeft className="size-4" />
          </button>
        )}
        {!edges.end && (
          <button
            type="button"
            onClick={() => page(1)}
            aria-label={t('templates.lanes.next')}
            className={cn(arrow, '-right-3')}
          >
            <ChevronRight className="size-4" />
          </button>
        )}
      </div>
    </section>
  )
}

// Everything a card shows, so "Wim Hof" or "Atomic Habits" finds its template.
function templateSearchText(tpl: TemplateSummary) {
  return [
    tpl.title,
    tpl.description,
    tpl.blueprint?.person,
    tpl.inspiredBy?.work,
    tpl.inspiredBy?.author,
    tpl.author?.handle,
    tpl.author?.name,
    ...(tpl.blueprint?.habitTitles ?? []),
    ...tpl.milestoneTitles,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

export type TemplateKind = 'goals' | 'habits'

// Habit templates are the blueprints. Everything else is a goal plan.
function isHabitTemplate(tpl: TemplateSummary) {
  return tpl.collection === 'blueprints' || !!tpl.blueprint
}

const GOAL_ONLY_FILTERS = new Set(['classics', 'esoteric', 'community'])

function matchesFilter(tpl: TemplateSummary, filter: TemplateFilter) {
  if (!filter) return true
  if (filter === 'mine') return tpl.mine
  if (filter === 'classics') return tpl.collection === 'classics'
  if (filter === 'blueprints') return tpl.collection === 'blueprints'
  if (filter === 'esoteric') return tpl.collection === 'esoteric'
  if (filter === 'community') return !!tpl.author
  return tpl.category === filter
}

type Lane = {
  key: string
  title: string
  hint?: string
  dot?: string
  items: TemplateSummary[]
}

// Blueprints, Classics, one lane per life area, then the community. A template shows once:
// in its collection lane, otherwise in its community or category lane.
function buildLanes(
  all: TemplateSummary[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): Lane[] {
  const inCollection = (tpl: TemplateSummary) =>
    tpl.collection === 'blueprints' || tpl.collection === 'classics'
  const rest = all.filter((tpl) => !inCollection(tpl) && !tpl.author)
  const lanes: Lane[] = [
    {
      key: 'blueprints',
      title: t('templates.bp.shelf'),
      hint: t('templates.bp.shelfHint'),
      items: all.filter((tpl) => tpl.collection === 'blueprints'),
    },
    {
      key: 'classics',
      title: t('templates.classics'),
      hint: t('templates.classicsHint'),
      items: all.filter((tpl) => tpl.collection === 'classics'),
    },
    ...CATEGORY_KEYS.map((key) => ({
      key,
      title: t(`discover.categories.${key}`),
      dot: CATEGORY_COLORS[key],
      items: rest.filter((tpl) => tpl.category === key),
    })),
    {
      key: 'community',
      title: t('templates.communityShelf'),
      hint: t('templates.communityHint'),
      items: all.filter((tpl) => !inCollection(tpl) && tpl.author),
    },
  ].filter((lane) => lane.items.length > 0)
  const seen = new Set(lanes.flatMap((lane) => lane.items.map((tpl) => tpl.id)))
  for (const key of new Set(all.filter((tpl) => !seen.has(tpl.id)).map((tpl) => tpl.category))) {
    const items = all.filter((tpl) => !seen.has(tpl.id) && tpl.category === key)
    if (items.length)
      lanes.push({
        key,
        title: t(`discover.categories.${key}`, { defaultValue: key }),
        dot: CATEGORY_COLORS[key],
        items,
      })
  }
  return lanes
}

// Habit templates, grouped by life area. The Habits tab already says what they are.
function buildHabitLanes(
  all: TemplateSummary[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): Lane[] {
  const lanes: Lane[] = CATEGORY_KEYS.map((key) => ({
    key,
    title: t(`discover.categories.${key}`),
    dot: CATEGORY_COLORS[key],
    items: all.filter((tpl) => tpl.category === key),
  })).filter((lane) => lane.items.length > 0)
  const seen = new Set(lanes.flatMap((lane) => lane.items.map((tpl) => tpl.id)))
  for (const key of new Set(all.filter((tpl) => !seen.has(tpl.id)).map((tpl) => tpl.category))) {
    const items = all.filter((tpl) => !seen.has(tpl.id) && tpl.category === key)
    if (items.length)
      lanes.push({
        key,
        title: t(`discover.categories.${key}`, { defaultValue: key }),
        dot: CATEGORY_COLORS[key],
        items,
      })
  }
  return lanes
}

function filterMeta(
  filter: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): { title: string; hint?: string; dot?: string } {
  if (filter === 'blueprints')
    return { title: t('templates.bp.shelf'), hint: t('templates.bp.shelfHint') }
  if (filter === 'classics')
    return { title: t('templates.classics'), hint: t('templates.classicsHint') }
  if (filter === 'esoteric')
    return { title: t('templates.esotericShelf'), hint: t('templates.esotericNote') }
  if (filter === 'community')
    return { title: t('templates.communityShelf'), hint: t('templates.communityHint') }
  if (filter === 'mine') return { title: t('templates.mine') }
  return { title: t(`discover.categories.${filter}`), dot: CATEGORY_COLORS[filter] }
}

function TemplateFilterChips({
  filter,
  onChange,
  hideMine = false,
  kind,
}: {
  filter: TemplateFilter
  onChange: (next: TemplateFilter) => void
  hideMine?: boolean
  kind?: TemplateKind
}) {
  const { t } = useTranslation()
  const chip = (selected: boolean) =>
    cn(
      'inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] transition-colors',
      selected
        ? 'border-primary/45 bg-primary/15 text-primary-text'
        : 'border-border bg-card text-muted-foreground hover:text-foreground',
    )
  return (
    <div
      data-noswipe
      className="-mx-4 flex gap-1.5 overflow-x-auto px-4 lg:mx-0 lg:px-0"
      style={{ scrollbarWidth: 'none' }}
    >
      <button type="button" onClick={() => onChange(null)} className={chip(!filter)}>
        {t('discover.allCategories')}
      </button>
      {kind !== 'habits' && (
        <>
          {/* On Goals this chip is a shortcut into the Habits tab (blueprints). */}
          <button
            type="button"
            onClick={() => onChange('blueprints')}
            className={chip(filter === 'blueprints')}
          >
            <Compass className="h-3 w-3" />
            {t('templates.bp.shelf')}
          </button>
          <button
            type="button"
            onClick={() => onChange('classics')}
            className={chip(filter === 'classics')}
          >
            <BookOpen className="h-3 w-3" />
            {t('templates.classics')}
          </button>
          <button
            type="button"
            onClick={() => onChange('esoteric')}
            className={chip(filter === 'esoteric')}
          >
            <Sparkles className="h-3 w-3" />
            {t('templates.esoteric')}
          </button>
          <button
            type="button"
            onClick={() => onChange('community')}
            className={chip(filter === 'community')}
          >
            {t('templates.communityShelf')}
          </button>
        </>
      )}
      {CATEGORY_KEYS.map((key) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={chip(filter === key)}
        >
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: CATEGORY_COLORS[key] }}
          />
          {t(`discover.categories.${key}`)}
        </button>
      ))}
      {!hideMine && (
        <button type="button" onClick={() => onChange('mine')} className={chip(filter === 'mine')}>
          {t('templates.mine')}
        </button>
      )}
    </div>
  )
}

export function DiscoverTemplates({
  filter,
  onFilterChange,
  cardAction,
  hideMine = false,
  kind: kindProp,
}: {
  filter: TemplateFilter
  onFilterChange: (f: TemplateFilter) => void
  cardAction?: CardAction
  hideMine?: boolean
  kind?: TemplateKind
}) {
  const { t, i18n } = useTranslation()
  const templates = useQuery(api.templates.list, { locale: i18n.language.split('-')[0] })
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<Sort>('popular')
  const [kind, setKind] = useState<TemplateKind>(
    kindProp ?? (filter === 'blueprints' ? 'habits' : 'goals'),
  )
  // A blueprint deep link opens Habits. A classics, esoteric or community link opens Goals.
  // An explicit kind (a coach opening the library) wins over the filter.
  useEffect(() => {
    if (kindProp) setKind(kindProp)
    else if (filter === 'blueprints') setKind('habits')
    else if (filter && GOAL_ONLY_FILTERS.has(filter)) setKind('goals')
  }, [filter, kindProp])
  // The Habits tab is the blueprint filter, so that chip does not narrow further.
  const activeFilter = kind === 'habits' && filter === 'blueprints' ? null : filter

  function selectKind(next: TemplateKind) {
    setKind(next)
    if (next === 'goals' && filter === 'blueprints') onFilterChange(null)
    if (next === 'habits' && filter && GOAL_ONLY_FILTERS.has(filter)) onFilterChange(null)
  }

  const shown = useMemo(() => {
    if (!templates) return []
    const q = query.trim().toLowerCase()
    return templates
      .filter((tpl) => (kind === 'habits' ? isHabitTemplate(tpl) : !isHabitTemplate(tpl)))
      .filter((tpl) => matchesFilter(tpl, activeFilter))
      .filter((tpl) => !q || templateSearchText(tpl).includes(q))
      .sort((a, b) => (sort === 'popular' ? b.useCount - a.useCount : b.createdAt - a.createdAt))
  }, [templates, query, activeFilter, sort, kind])

  const lanes = useMemo(
    () =>
      activeFilter || query.trim()
        ? []
        : kind === 'habits'
          ? buildHabitLanes(shown, t)
          : buildLanes(shown, t),
    [shown, activeFilter, query, t, kind],
  )

  return (
    <div className="flex flex-col gap-3">
      <div
        role="tablist"
        className="flex w-full rounded-xl bg-muted p-1 text-sm font-bold sm:w-fit"
      >
        {(['goals', 'habits'] as const).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={kind === key}
            onClick={() => selectKind(key)}
            className={cn(
              'flex-1 cursor-pointer rounded-lg px-4 py-1.5 transition-colors sm:flex-none',
              kind === key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t(`templates.tabs.${key}`)}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <label className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-border bg-card px-2.5 py-2 text-muted-foreground/80 focus-within:border-foreground/25">
          <Search className="h-3.5 w-3.5 shrink-0" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('templates.search')}
            className="min-w-0 flex-1 bg-transparent text-[12.5px] text-foreground outline-none placeholder:text-muted-foreground/70"
          />
        </label>
        {/* Menu, not a native <select>: the native picker misplaces itself in mobile web views. */}
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <button
                type="button"
                aria-label={t('templates.sortLabel')}
                className="flex shrink-0 cursor-pointer items-center gap-1 rounded-lg px-1.5 py-1 text-[11.5px] text-foreground/70 transition-colors hover:text-foreground"
              />
            }
          >
            {sort === 'popular' ? t('templates.sortPopular') : t('templates.sortNew')}
            <ChevronDown className="h-3 w-3" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-36">
            {(['popular', 'new'] as const).map((s) => (
              <DropdownMenuItem key={s} className="cursor-pointer" onClick={() => setSort(s)}>
                <Check className={cn('h-4 w-4', sort !== s && 'invisible')} />
                {s === 'popular' ? t('templates.sortPopular') : t('templates.sortNew')}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <TemplateFilterChips
        filter={activeFilter}
        onChange={onFilterChange}
        hideMine={hideMine}
        kind={kind}
      />
      {templates === undefined ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))] gap-2.5">
          {[70, 55, 80, 62].map((w) => (
            <div
              key={w}
              className="flex flex-col gap-2 rounded-2xl border border-border bg-card p-3"
            >
              <div className="h-2.5 w-16 animate-pulse rounded bg-muted" />
              <div className="h-3.5 animate-pulse rounded bg-muted" style={{ width: `${w}%` }} />
              <div className="h-2.5 w-24 animate-pulse rounded bg-muted/70" />
              {[80, 65, 72].map((m) => (
                <div
                  key={m}
                  className="h-2 animate-pulse rounded bg-muted/50"
                  style={{ width: `${m}%` }}
                />
              ))}
              <div className="mt-1 flex justify-between border-t border-border pt-2">
                <div className="h-2.5 w-20 animate-pulse rounded bg-muted/60" />
                <div className="h-2.5 w-8 animate-pulse rounded bg-muted/60" />
              </div>
            </div>
          ))}
        </div>
      ) : shown.length === 0 ? (
        <p className="py-16 text-center text-sm text-muted-foreground">
          {query.trim()
            ? t('templates.emptySearch')
            : activeFilter === 'mine'
              ? t('templates.emptyMine')
              : t('templates.empty')}
        </p>
      ) : lanes.length > 0 ? (
        <div className="flex flex-col gap-8">
          {lanes.map((lane) => (
            <Shelf
              key={lane.key}
              lane
              title={lane.title}
              hint={lane.hint}
              dot={lane.dot}
              items={lane.items}
              action={cardAction}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))] gap-2.5">
          {shown.map((tpl) => (
            <TemplateCard key={tpl.id} tpl={tpl} action={cardAction} />
          ))}
        </div>
      )}
    </div>
  )
}

const LANE_LIMIT = 12

function seeAllSearch(filter: string): { tab: 'templates'; mine?: true; filter?: string } {
  return filter === 'mine' ? { tab: 'templates', mine: true } : { tab: 'templates', filter }
}

// The new-goal screen's template swimlanes: Blueprints, Classics, one per category, then the
// community. A template shows once. The same chips as the templates screen narrow it to one group.
export function TemplateLanes({
  onSelect,
  seeAll = true,
}: {
  onSelect?: (tpl: TemplateSummary) => void
  seeAll?: boolean
}) {
  const { t, i18n } = useTranslation()
  const templates = useQuery(api.templates.list, { locale: i18n.language.split('-')[0] })
  const [filter, setFilter] = useState<TemplateFilter>(null)
  const sorted = useMemo(
    () => [...(templates ?? [])].sort((a, b) => b.useCount - a.useCount),
    [templates],
  )
  const lanes = useMemo(() => buildLanes(sorted, t), [sorted, t])
  const filtered = useMemo(
    () => (filter ? sorted.filter((tpl) => matchesFilter(tpl, filter)) : []),
    [sorted, filter],
  )
  if (!sorted.length) return null
  const meta = filter ? filterMeta(filter, t) : null
  const seeAllLink = (key: string) =>
    seeAll && (
      <Link
        to="/explore"
        search={seeAllSearch(key)}
        className="shrink-0 text-[13px] font-bold text-primary-text underline-offset-4 hover:underline"
      >
        {t('planner.seeAllTemplates')}
      </Link>
    )
  return (
    <div className="flex w-full flex-col gap-4">
      <TemplateFilterChips filter={filter} onChange={setFilter} />
      {filter ? (
        filtered.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            {filter === 'mine' ? t('templates.emptyMine') : t('templates.empty')}
          </p>
        ) : (
          <Shelf
            lane
            title={meta?.title ?? ''}
            hint={meta?.hint}
            dot={meta?.dot}
            items={filtered}
            onSelect={onSelect}
            more={seeAllLink(filter)}
          />
        )
      ) : (
        <div className="flex flex-col gap-8">
          {lanes.map((lane) => (
            <Shelf
              key={lane.key}
              lane
              title={lane.title}
              hint={lane.hint}
              dot={lane.dot}
              items={lane.items.slice(0, LANE_LIMIT)}
              onSelect={onSelect}
              more={seeAllLink(lane.key)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
